import { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, Tabs, Tab } from '@mui/material';
import { Bar, Pie } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { request } from '../../../../utils';
import InventoryIcon from '@mui/icons-material/Inventory';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import DangerousIcon from '@mui/icons-material/Dangerous';
import { useNavigate } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

export const INV = () => {
  const [loading, setLoading] = useState(true);
  const [barTab, setBarTab] = useState(0);
  const [pieTab, setPieTab] = useState(0);
  const [barChartData, setBarChartData] = useState({ labels: [], datasets: [] });
  const [pieChartData, setPieChartData] = useState({ labels: [], datasets: [] });
  const [totalExpItems, setTotalExpItems] = useState(0);
  const [stockValue, setStockValue] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTotalExpItems = async () => {
      setLoading(true);
      try {
        const response = await request('/total-expired-items', { method: 'GET' });
        if (response?.success) {
          setTotalExpItems(response.totalExpiredItems);
        } else {
          toast('Failed to load expired items', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error fetching Data:', error);
        toast('Error fetching expired items', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchTotalExpItems();
  }, []);

  useEffect(() => {
    const fetchStockStatus = async () => {
      setLoading(true);
      try {
        const response = await request('/total-stock-status', { method: 'GET' });
        if (response?.success) {
          setStockValue(response.totalPurAmount);
        } else {
          toast('Failed to load stock value', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error fetching Data:', error);
        toast('Error fetching stock status', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchStockStatus();
  }, []);

  useEffect(() => {
    const fetchRevenue = async () => {
      setLoading(true);
      try {
        const response = await request('/total-revenue', { method: 'GET' });
        if (response?.success) {
          setTotalRevenue(response.totalRevenue);
        } else {
          toast('Failed to load revenue', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error fetching Data:', error);
        toast('Error fetching revenue', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchRevenue();
  }, []);

  useEffect(() => {
    const fetchSalesPaymode = async () => {
      setLoading(true);
      try {
        const response = await request('/total-sales-paymode', { method: 'GET' });
        if (response?.success && response.data) {
          const { daily, weekly, monthly } = response.data;
          const selectedData = [daily, weekly, monthly][pieTab];
          const payModeSums = { CASH: 0, Credit: 0, Bank: 0 };
          selectedData.forEach((entry) => {
            payModeSums.CASH += entry.CASH || 0;
            payModeSums.Credit += entry.Credit || 0;
            payModeSums.Bank += entry.Bank || 0;
          });
          const newPieChartData = {
            labels: ['CASH', 'Credit', 'Bank'],
            datasets: [
              {
                data: [payModeSums.CASH, payModeSums.Credit, payModeSums.Bank],
                backgroundColor: ['#FF0090', '#FFD700', '#48D1CC'],
                hoverOffset: 8
              }
            ]
          };
          setPieChartData(newPieChartData);
        } else {
          toast('Failed to load sales paymode data', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error fetching pie chart data:', error);
        toast('Error fetching pie chart data', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchSalesPaymode();
  }, [pieTab]);

  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);
      try {
        const [purchaseRes, salesRes] = await Promise.all([
          request('/total-purchase', { method: 'GET' }),
          request('/total-sales', { method: 'GET' })
        ]);

        if (purchaseRes?.success && salesRes?.success) {
          const purchaseData = [purchaseRes.data.daily, purchaseRes.data.weekly, purchaseRes.data.monthly][barTab];
          const salesData = [salesRes.data.daily, salesRes.data.weekly, salesRes.data.monthly][barTab];

          const labels = purchaseData.map((item) => {
            if (barTab === 0) return `${item.dayName} (${item.date})`;
            return item.label;
          });

          const purchaseMap = new Map(
            purchaseData.map((item) => {
              const label = barTab === 0 ? `${item.dayName} (${item.date})` : item.label;
              return [label, item.totalNetAmount];
            })
          );

          const salesMap = new Map(
            salesData.map((item) => {
              const label = barTab === 0 ? `${item.dayName} (${item.date})` : item.label;
              return [label, item.totalNetAmount];
            })
          );

          const unifiedLabels = [...new Set([...purchaseMap.keys(), ...salesMap.keys()])];

          const combinedChartData = {
            labels: unifiedLabels,
            datasets: [
              {
                label: 'Purchase',
                data: unifiedLabels.map((label) => purchaseMap.get(label) || 0),
                backgroundColor: '#48D1CC', // Ocean Green
                borderColor: '#E6E6FA'
              },
              {
                label: 'Sales',
                data: unifiedLabels.map((label) => salesMap.get(label) || 0),
                backgroundColor: '#B0C4DE', // Ocean Green
                borderColor: '#48D1CC'
              }
            ]
          };

          setBarChartData(combinedChartData);
        } else {
          toast('Failed to load chart data', { variant: 'error' });
        }
      } catch (err) {
        console.error(err);
        toast('Error loading chart data', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [barTab]);

  return (
    <Box sx={{ flex: 1, p: 2, background: 'linear-gradient(to right, #e0f7fa, #f4f6f8)' }}>
      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={4}>
          <Card sx={{...cardStyle,cursor:"pointer"}}>
            <CardContent>
              <InventoryIcon sx={iconStyle} />
              <Typography variant="h4" sx={textStyle}>
                Inventory Status
              </Typography>
              <Typography variant="h4" sx={textStyle}>
                ₹{stockValue}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{...cardStyle,cursor:"pointer"}} onClick={()=> navigate('/hospital/inventory/product-info')} >
            <CardContent>
              <DangerousIcon sx={iconStyle} />
              <Typography variant="h4" sx={textStyle}>
                Expiry Medicine
              </Typography>
              <Typography variant="h4" sx={textStyle}>
                {totalExpItems}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{...cardStyle,cursor:"pointer"}}>
            <CardContent>
              <CurrencyRupeeIcon sx={iconStyle} />
              <Typography variant="h4" sx={textStyle}>
                Revenue
              </Typography>
              <Typography variant="h4" sx={textStyle}>
                ₹{totalRevenue}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      
        {/* Chart Tabs */}
        <Grid item xs={6}>
          <Card sx={chartCardStyle}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
              <Tabs value={barTab} onChange={(e, val) => setBarTab(val)} aria-label="Bar Chart Tabs">
                <Tab label="Daily" />
                <Tab label="Weekly" />
                <Tab label="Monthly" />
              </Tabs>
            </Box>
            <CardContent sx={{ pt: 1, pb: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 900 }}>
                Purchase vs Sales Revenue
              </Typography>
              <Box sx={{ height: '260px' ,cursor:'pointer'}}>
                <Bar
                  data={barChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: { stepSize: 20, precision: 0 }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{...chartCardStyle,cursor:"pointer"}}>
            <CardContent sx={{ p: 1 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider'}}>
                <Tabs value={pieTab} onChange={(e, val) => setPieTab(val)} aria-label="Pie Chart Tabs">
                  <Tab label="Daily" />
                  <Tab label="Weekly" />
                  <Tab label="Monthly" />
                </Tabs>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '298px',
                  mx: 'auto',
                  pt: 2
                }}
              >
                <Pie data={pieChartData} options={{ maintainAspectRatio: true }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const cardStyle = {
  background: 'linear-gradient(135deg, #4ca1af, #c4e0e5)',
  borderRadius: '12px',
  boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)',
  transition: '0.3s',
  border: '0.5px solid #ccc',
  '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' }
};

const iconStyle = {
  fontSize: 60,
  color: 'white',
  borderRadius: '8px'
};

const textStyle = {
  color: 'white',
  fontWeight: 'bold'
};

const chartCardStyle = {
  p: 2,
  borderRadius: '10px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  transition: '0.3s'
};
